import { useEffect, useMemo, useState, useRef } from "react";
import { isPurchaseUser } from "../utils/permissions";
import { authFetch } from "../utils/authFetch";
import {
  Search,
  Plus,
  X,
  Save,
  IndianRupee,
  Building2,
  Package,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Tags,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_API_URL}`;

function PriceListPage() {
  const [vendors, setVendors] = useState([]);
  const [materials, setMaterials] = useState([]); 
  const [priceList, setPriceList] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // SEARCHABLE DROP-DOWN STATES FOR 2500+ ITEMS
  const [materialSearch, setMaterialSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // SEARCHABLE DROP-DOWN STATES FOR 1000+ VENDORS
  const [vendorSearch, setVendorSearch] = useState("");
  const [isVendorDropdownOpen, setIsVendorDropdownOpen] = useState(false);
  const vendorDropdownRef = useRef(null);

  const [form, setForm] = useState({
    material_id: "", 
    vendor_id: "",
    rate: "",
    category: "",
    remarks: "",
  });

  const itemsPerPage = 10;

  const t = {
    bg: "#f8fafc",
    surface: "#ffffff",
    card: "#ffffff",
    border: "#e2e8f0",
    borderSoft: "#f1f5f9",
    text: "#0f172a",
    textMuted: "#64748b",
    inputBg: "#ffffff",
    primary: "#4f46e5", 
    primaryHover: "#4338ca",
    success: "#10b981",
    danger: "#ef4444",
    warning: "#f59e0b"
  };

  useEffect(() => {
    loadInitialData();
    
    function handleClickOutside(event) {
      // Close item dropdown if clicked outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      // Close vendor dropdown if clicked outside
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setIsVendorDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadInitialData = async () => {
    setIsRefreshing(true);
    await Promise.all([loadVendors(), loadMaterials(), loadPriceList()]);
    setIsRefreshing(false);
  };

  const loadVendors = async () => {
    try {
      const res = await authFetch(`${API}/vendors`);
      if (res.ok) setVendors(await res.json());
    } catch (err) {
      console.error("Error loading vendors:", err);
    }
  };

  const loadMaterials = async () => {
    try {
      const res = await authFetch(`${API}/materials`); 
      if (res.ok) setMaterials(await res.json());
    } catch (err) {
      console.error("Error loading materials master:", err);
    }
  };

  const loadPriceList = async () => {
    try {
      const res = await authFetch(`${API}/price-list`);
      if (res.ok) setPriceList(await res.json());
    } catch (err) {
      console.error("Error loading price list:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectMaterial = (material) => {
    setForm((prev) => ({
      ...prev,
      material_id: material.id,
      category: material.category || "",
    }));
    setMaterialSearch(material.material_name || material.item_name || material.name || "");
    setIsDropdownOpen(false);
  };

  const handleSelectVendor = (vendor) => {
    setForm((prev) => ({
      ...prev,
      vendor_id: vendor.id,
    }));
    setVendorSearch(vendor.name || "");
    setIsVendorDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.material_id || !form.vendor_id || !form.rate) {
      alert("Please fill in Material, Vendor, and Rate fields.");
      return;
    }

    const matchedMaterial = materials.find((m) => String(m.id) === String(form.material_id));
    const resolvedTextName = matchedMaterial 
      ? (matchedMaterial.material_name || matchedMaterial.item_name || matchedMaterial.name) 
      : "";

    const payload = {
      item_name: resolvedTextName,
      vendor_id: form.vendor_id,
      rate: form.rate,
      category: form.category,
      remarks: form.remarks
    };

    try {
      const res = await authFetch(`${API}/add-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setForm({ material_id: "", vendor_id: "", rate: "", category: "", remarks: "" });
        setMaterialSearch("");
        setVendorSearch("");
        setShowForm(false);
        loadPriceList();
      } else {
        const errData = await res.json();
        alert(`Error: ${errData.error || "Failed to save entry."}`);
      }
    } catch (err) {
      console.error("Error saving price entry:", err);
    }
  };

  const handleDeletePrice = async (id) => {
    if (!window.confirm("Are you sure you want to clear this price record?")) return;
    try {
      const res = await authFetch(`${API}/price-list/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        loadPriceList();
      }
    } catch (err) {
      console.error("Error deleting price row record:", err);
    }
  };

  const filteredMaterialOptions = useMemo(() => {
    if (!materialSearch) return materials;
    return materials.filter(m => {
      const name = m.material_name || m.item_name || m.name || "";
      return name.toLowerCase().includes(materialSearch.toLowerCase());
    });
  }, [materials, materialSearch]);

  // Filter 1000+ vendors dynamically inside the input box
  const filteredVendorOptions = useMemo(() => {
    if (!vendorSearch) return vendors;
    return vendors.filter(v => {
      const name = v.name || "";
      return name.toLowerCase().includes(vendorSearch.toLowerCase());
    });
  }, [vendors, vendorSearch]);

  const groupedItems = useMemo(() => {
    const groups = {};
    priceList.forEach((item) => {
      const resolvedItemName = item.item_name || `Material ID: ${item.id}`;
      const matchedVendor = vendors.find(v => String(v.id) === String(item.vendor_id));
      const vendorName = matchedVendor ? matchedVendor.name : (item.vendor_name || `Vendor ID: ${item.vendor_id}`);

      if (!groups[resolvedItemName]) {
        groups[resolvedItemName] = {
          item_name: resolvedItemName,
          category: item.category || "-",
          vendorQuotes: [],
        };
      }

      groups[resolvedItemName].vendorQuotes.push({
        id: item.id,
        vendor_id: item.vendor_id,
        vendor_name: vendorName,
        rate: Number(item.rate || 0),
        remarks: item.remarks || ""
      });
    });

    return Object.values(groups).map((group) => {
      const sortedQuotes = [...group.vendorQuotes].sort((a, b) => a.rate - b.rate);
      const rates = sortedQuotes.map(q => q.rate);
      return {
        ...group,
        lowestPrice: rates.length > 0 ? Math.min(...rates) : 0,
        highestPrice: rates.length > 0 ? Math.max(...rates) : 0,
        vendorQuotes: sortedQuotes
      };
    });
  }, [priceList, vendors]);

  const selectedItemStats = useMemo(() => {
    if (!form.material_id) return { lowest: "Select Item", highest: "Select Item", count: 0 };
    
    const matchedMaterial = materials.find((m) => String(m.id) === String(form.material_id));
    if (!matchedMaterial) return { lowest: "No quotes yet", highest: "No quotes yet", count: 0 };

    const targetName = (matchedMaterial.material_name || matchedMaterial.item_name || matchedMaterial.name || "").toLowerCase();
    const specificQuotes = priceList.filter(p => (p.item_name || "").toLowerCase() === targetName);
    
    if (specificQuotes.length === 0) {
      return { lowest: "No quotes yet", highest: "No quotes yet", count: 0 };
    }

    const rates = specificQuotes.map(q => Number(q.rate || 0));
    return {
      lowest: `₹${Math.min(...rates)}`,
      highest: `₹${Math.max(...rates)}`,
      count: specificQuotes.length
    };
  }, [form.material_id, materials, priceList]);

  const filteredGroups = useMemo(() => {
    return groupedItems.filter((group) => {
      const matchesSearch = 
        group.item_name.toLowerCase().includes(search.toLowerCase()) ||
        group.category.toLowerCase().includes(search.toLowerCase());
      const matchesVendor = vendorFilter
        ? group.vendorQuotes.some(q => String(q.vendor_id) === String(vendorFilter))
        : true;
      return matchesSearch && matchesVendor;
    });
  }, [groupedItems, search, vendorFilter]);

  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const paginatedGroups = useMemo(() => {
    return filteredGroups.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredGroups, currentPage]);

  const s = {
    page: { minHeight: "100vh", background: t.bg, padding: "32px 24px", fontFamily: "'Inter', system-ui, sans-serif" },
    wrap: { width: "100%", maxWidth: "1400px", margin: "0 auto", position: "relative" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" },
    title: { fontSize: "26px", fontWeight: "700", color: t.text, letterSpacing: "-0.02em" },
    sub: { fontSize: "13px", color: t.textMuted, marginTop: "4px" },
    btnGroup: { display: "flex", gap: "10px", alignItems: "center" },
    primaryBtn: { height: "42px", padding: "0 18px", borderRadius: "10px", border: "none", background: t.primary, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600" },
    secondaryBtn: { height: "42px", padding: "0 14px", borderRadius: "10px", border: `1px solid ${t.border}`, background: "#fff", color: t.text, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: "500" },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "20px", marginBottom: "24px" },
    statCard: { background: t.card, border: `1px solid ${t.border}`, borderRadius: "16px", padding: "20px", boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)" },
    statLabel: { fontSize: "12px", fontWeight: "500", color: t.textMuted, marginBottom: "8px", textTransform: "uppercase" },
    statValue: { fontSize: "24px", fontWeight: "700", color: t.text },
    section: { background: t.surface, border: `1px solid ${t.border}`, borderRadius: "16px", marginBottom: "24px" },
    sectionHead: { padding: "20px 24px", borderBottom: `1px solid ${t.borderSoft}`, display: "flex", justifyContent: "space-between", alignItems: "center" },
    formGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "20px", padding: "24px" },
    label: { display: "block", fontSize: "12px", fontWeight: "600", color: t.text, marginBottom: "6px" },
    input: { width: "100%", height: "42px", borderRadius: "10px", border: `1px solid ${t.border}`, background: "#fff", padding: "0 14px", fontSize: "14px", outline: "none", boxSizing: "border-box" },
    searchBox: { display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: `1px solid ${t.border}`, borderRadius: "10px", padding: "0 14px", height: "42px", width: "320px" },
    searchInput: { flex: 1, border: "none", outline: "none", background: "transparent", fontSize: "14px" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { padding: "14px 24px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: t.textMuted, background: t.borderSoft, borderBottom: `1px solid ${t.border}` },
    td: { padding: "20px 24px", borderBottom: `1px solid ${t.borderSoft}`, fontSize: "14px", color: t.text, verticalAlign: "top" },
  };

  return (
    <div style={s.page}>
      <div style={s.wrap}>
        
        {/* HEADER */}
        <div style={s.header}>
          <div>
            <div style={s.title}>Procurement Price Comparison Matrix</div>
            <div style={s.sub}>Comparing active market vendor channels for commercial fitouts</div>
          </div>
          <div style={s.btnGroup}>
            <button onClick={loadInitialData} style={s.secondaryBtn}>
              <RefreshCw size={15} style={{ marginRight: '4px' }} /> Sync Matrix
            </button>
            <button onClick={() => setShowForm(!showForm)} style={s.primaryBtn}>
              {showForm ? <><X size={15} /> Close</> : <><Plus size={15} /> Add Vendor Price</>}
            </button>
          </div>
        </div>

        {/* METRICS PANELS */}
        <div style={s.statsGrid}>
          <div style={s.statCard}>
            <div style={s.statLabel}>Unique Materials Listed</div>
            <div style={s.statValue}>{groupedItems.length}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Total Active Market Quotes</div>
            <div style={s.statValue}>{priceList.length}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Selected Item Min Rate</div>
            <div style={{...s.statValue, color: selectedItemStats.count > 0 ? t.success : t.textMuted}}>
              {selectedItemStats.lowest}
            </div>
          </div>
          <div style={s.statCard}>
            <div style={s.statLabel}>Selected Item Max Rate</div>
            <div style={{...s.statValue, color: selectedItemStats.count > 0 ? t.danger : t.textMuted}}>
              {selectedItemStats.highest}
            </div>
          </div>
        </div>

        {/* INPUT FORM ENTRY */}
        {showForm && (
          <div style={s.section}>
            <div style={s.sectionHead}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "700" }}>Link Vendor Base Rate</div>
                <div style={{ fontSize: "12px", color: t.textMuted, marginTop: "4px" }}>Bind items directly to custom provider quotes</div>
              </div>
              <button onClick={handleSubmit} style={s.primaryBtn}>
                <Save size={15} /> Save Quote
              </button>
            </div>
            
            <div style={s.formGrid}>
              {/* MATERIAL AUTOCOMPLETE DROPDOWN */}
              <div style={{ position: "relative" }} ref={dropdownRef}>
                <label style={s.label}>Select Material Item</label>
                <div style={{ position: "relative" }}>
                  <Package size={16} style={{ position: "absolute", left: "12px", top: "12px", color: t.textMuted, zIndex: 2 }} />
                  <input
                    type="text"
                    placeholder="Type to search 2500+ items (e.g. 20AMPS)..."
                    value={materialSearch}
                    onChange={(e) => {
                      setMaterialSearch(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    style={{ ...s.input, paddingLeft: "40px", paddingRight: "32px" }}
                  />
                  <ChevronDown size={16} style={{ position: "absolute", right: "12px", top: "13px", color: t.textMuted, cursor: "pointer" }} onClick={() => setIsDropdownOpen(!isDropdownOpen)} />
                </div>

                {isDropdownOpen && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#ffffff",
                    border: `1px solid ${t.border}`,
                    borderRadius: "10px",
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                    maxHeight: "260px",
                    overflowY: "auto",
                    zIndex: 999,
                    marginTop: "4px"
                  }}>
                    {filteredMaterialOptions.length > 0 ? (
                      filteredMaterialOptions.map((m) => {
                        const nameText = m.material_name || m.item_name || m.name;
                        return (
                          <div
                            key={m.id}
                            onClick={() => handleSelectMaterial(m)}
                            style={{
                              padding: "10px 14px",
                              fontSize: "13px",
                              cursor: "pointer",
                              color: t.text,
                              borderBottom: `1px solid ${t.borderSoft}`,
                              backgroundColor: String(form.material_id) === String(m.id) ? "#f1f5f9" : "transparent"
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
                            onMouseLeave={(e) => e.target.style.backgroundColor = String(form.material_id) === String(m.id) ? "#f1f5f9" : "transparent"}
                          >
                            {nameText}
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: "12px", fontSize: "13px", color: t.textMuted, textAlign: "center" }}>
                        No matching item found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ─── HUMAN SEARCHABLE COMBOBOX VENDOR STEP (Replaced image_593f02.jpg structure) ─── */}
              <div style={{ position: "relative" }} ref={vendorDropdownRef}>
                <label style={s.label}>Assigned Vendor</label>
                <div style={{ position: "relative" }}>
                  <Building2 size={16} style={{ position: "absolute", left: "12px", top: "12px", color: t.textMuted, zIndex: 2 }} />
                  <input
                    type="text"
                    placeholder="Type to search 1000+ vendors..."
                    value={vendorSearch}
                    onChange={(e) => {
                      setVendorSearch(e.target.value);
                      setIsVendorDropdownOpen(true);
                    }}
                    onFocus={() => setIsVendorDropdownOpen(true)}
                    style={{ ...s.input, paddingLeft: "40px", paddingRight: "32px" }}
                  />
                  <ChevronDown size={16} style={{ position: "absolute", right: "12px", top: "13px", color: t.textMuted, cursor: "pointer" }} onClick={() => setIsVendorDropdownOpen(!isVendorDropdownOpen)} />
                </div>

                {isVendorDropdownOpen && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#ffffff",
                    border: `1px solid ${t.border}`,
                    borderRadius: "10px",
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                    maxHeight: "260px",
                    overflowY: "auto",
                    zIndex: 999,
                    marginTop: "4px"
                  }}>
                    {filteredVendorOptions.length > 0 ? (
                      filteredVendorOptions.map((v) => (
                        <div
                          key={v.id}
                          onClick={() => handleSelectVendor(v)}
                          style={{
                            padding: "10px 14px",
                            fontSize: "13px",
                            cursor: "pointer",
                            color: t.text,
                            borderBottom: `1px solid ${t.borderSoft}`,
                            backgroundColor: String(form.vendor_id) === String(v.id) ? "#f1f5f9" : "transparent"
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = String(form.vendor_id) === String(v.id) ? "#f1f5f9" : "transparent"}
                        >
                          {v.name}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "12px", fontSize: "13px", color: t.textMuted, textAlign: "center" }}>
                        No matching vendor found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* CATEGORY OUTLET */}
              <div>
                <label style={s.label}>Category</label>
                <div style={{ position: "relative" }}>
                  <Tags size={16} style={{ position: "absolute", left: "12px", top: "12px", color: t.textMuted }} />
                  <input name="category" value={form.category} readOnly placeholder="Auto-resolved from Master" style={{ ...s.input, paddingLeft: "40px", background: "#f8fafc", cursor: "not-allowed" }} />
                </div>
              </div>

              {/* NEGOTIATED RATE */}
              <div>
                <label style={s.label}>Negotiated Rate</label>
                <div style={{ position: "relative" }}>
                  <IndianRupee size={16} style={{ position: "absolute", left: "12px", top: "12px", color: t.textMuted }} />
                  <input name="rate" type="number" value={form.rate} onChange={handleChange} placeholder="0.00" style={{ ...s.input, paddingLeft: "40px" }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* COMPARISON DATA MATRIX VIEW PLATFORM */}
        <div style={s.section}>
          <div style={s.sectionHead}>
            <div>
              <div style={{ fontSize: "16px", fontWeight: "700" }}>Active Procurement Matrix</div>
              <div style={{ fontSize: "12px", color: t.textMuted, marginTop: "4px" }}>Itemized cross-channel supplier analytics</div>
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <div style={s.searchBox}>
                <Search size={16} color={t.textMuted} />
                <input
                  placeholder="Chase item (e.g. 20AMPS ONE WAY)..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  style={s.searchInput}
                />
              </div>

              <select
                value={vendorFilter}
                onChange={(e) => { setVendorFilter(e.target.value); setCurrentPage(1); }}
                style={{ ...s.input, width: "240px" }}
              >
                <option value="">All Filtered Channels</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={{ ...s.th, width: "30%" }}>Material Description</th>
                  <th style={{ ...s.th, width: "12%" }}>Category</th>
                  <th style={{ ...s.th, width: "13%" }}>Lowest Market Price</th>
                  <th style={{ ...s.th, width: "13%" }}>Highest Market Price</th>
                  <th style={{ ...s.th, width: "32%" }}>Vendor Channel Breakdown (Sorted Lowest First)</th>
                </tr>
              </thead>
              <tbody>
                {paginatedGroups.length > 0 ? (
                  paginatedGroups.map((group, index) => (
                    <tr key={index} style={{ background: index % 2 === 0 ? "#ffffff" : "#f8fafc" }}>
                      <td style={{ ...s.td, fontWeight: "600", color: t.text, fontSize: "14px", lineHeight: "1.5" }}>
                        {group.item_name}
                      </td>
                      <td style={s.td}>
                        <span style={{ color: "#334155", background: "#e2e8f0", padding: "4px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: "600" }}>
                          {group.category}
                        </span>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", color: t.success, fontWeight: "700", fontSize: "15px" }}>
                          <TrendingDown size={14} />
                          ₹{group.lowestPrice}
                        </div>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", color: t.danger, fontWeight: "600", fontSize: "15px" }}>
                          <TrendingUp size={14} />
                          ₹{group.highestPrice}
                        </div>
                      </td>
                      <td style={s.td}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {group.vendorQuotes.map((quote) => {
                            const isLowest = quote.rate === group.lowestPrice;
                            return (
                              <div 
                                key={quote.id} 
                                style={{ 
                                  display: "flex", 
                                  justifyContent: "space-between", 
                                  alignItems: "center",
                                  padding: "6px 12px",
                                  borderRadius: "8px",
                                  background: isLowest ? "#f0fdf4" : "#ffffff",
                                  border: isLowest ? `1px solid #bbf7d0` : `1px solid ${t.borderSoft}`,
                                }}
                              >
                                <span style={{ fontSize: "12px", fontWeight: isLowest ? "600" : "500", color: isLowest ? "#166534" : t.text }}>
                                  {quote.vendor_name} {isLowest && "🏆"}
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <span style={{ fontSize: "13px", fontWeight: "700", color: isLowest ? "#15803d" : t.text }}>
                                    ₹{quote.rate}
                                  </span>
                                 {!isPurchaseUser() && (
  <Trash2 
    size={13} 
    color={t.textMuted} 
    style={{ cursor: "pointer", opacity: 0.7 }}
    onClick={() => handleDeletePrice(quote.id)}
  />
)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ ...s.td, textAlign: "center", padding: "48px 24px", color: t.textMuted }}>
                      No interior material items found matching criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION PANEL */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", background: "#ffffff", borderTop: `1px solid ${t.border}` }}>
            <div style={{ fontSize: "13px", color: t.textMuted }}>
              Showing {paginatedGroups.length} of {filteredGroups.length} unique interior items
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((c) => Math.max(c - 1, 1))}
                style={{ ...s.secondaryBtn, height: "36px", padding: "0 8px", opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                <ChevronLeft size={14} />
              </button>
              <div style={{ fontSize: "13px", fontWeight: "600", color: t.text }}>
                {currentPage} / {totalPages || 1}
              </div>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((c) => Math.min(c + 1, totalPages))}
                style={{ ...s.secondaryBtn, height: "36px", padding: "0 8px", opacity: currentPage >= totalPages ? 0.5 : 1, cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default PriceListPage;